import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import ReactPlayer from "react-player";
import TranscriptTable from "../../components/transcript-table";
import ViewQuestionsModal from "../../components/view-questions-modal";
import { toast } from "react-toastify";
import EditPromptModal from "../../components/edit-prompt-modal";
import ContentTable from "../../components/content-table";
import { isAdminRole, isTeacherRole } from "../../shared/utils";
import DeleteConfirmationModal from "../../components/delete-confirmation-modal";
import { useAccess } from "../../contexts/AccessContext";
import {useQuery, useMutation} from "@tanstack/react-query"

const VideoGuideSettings = () => {
  const [played, setPlayed] = useState(0); // range: 0.0 - 1.0
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const [questionConfirmation, setQuestionConfirmation] = useState(false);
  const [chapterConfirmation, setChapterConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const chapterRegeneratingRef = useRef(false);
  const chapterPollIntervalRef = useRef(null);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (chapterPollIntervalRef.current) {
        clearInterval(chapterPollIntervalRef.current);
      }
    };
  }, []);

  
  const { hasEdit } = useAccess();
  const isAdmin = useMemo(() => isAdminRole(), []);

  const togglePlayPause = () => {
    setPlaying((prev) => !prev);
  };

  const handleProgress = (progress) => {
    setPlayed(progress.played);
    setPlayedSeconds(progress.playedSeconds);
    setVideoProgress(progress.playedSeconds);
  };

  const handleDuration = (dur) => {
    setDuration(dur);
  };

  const handleSeekChange = (e) => {
    const newPlayed = parseFloat(e.target.value);
    setPlayed(newPlayed);
    ref.current.seekTo(newPlayed);
  };

  const [sourceType, _setSourceType] = useState("course-content");
  const setSourceType = useCallback((val) => {
    setVideoData();
    _setSourceType(val);
    setUrlInputValue("");
    setSelectedSource({
      category: "",
      lesson: "",
      course: "",
      unit: "",
      url: "",
    });
    setCurrentVideoCode("");
  }, []);

  const [urlInputValue, setUrlInputValue] = useState("");
  const [selectedSource, setSelectedSource] = useState({
    category: "",
    lesson: "",
    course: "",
    unit: "",
    url: "",
  });

  const [currentVideoCode, setCurrentVideoCode] = useState("");
  const [tableView, setTableView] = useState("transcript");

  // const [categoryArray, setCategoryArray] = useState([]);
  // const [courseArray, setCourseArray] = useState([]);
  // const [unitArray, setUnitArray] = useState([]);
  // const [lessonArray, setLessonArray] = useState([]);
  // const [urlArray, setUrlArray] = useState([]);


  let lessonId = null;
  // // Only resolve lesson when it exists
  // if (selectedSource?.lesson) {
  //   const lesson = lessons.find(
  //     (l) => l.Id.toString() === selectedSource.lesson
  //   );
  //   lessonId = lesson?.cm_id; // safe
  // }


  const getVideoName = useCallback(async (videoCode) => {
    try {
      if (!/^[a-zA-Z0-9_-]{11}$/.test(videoCode)) {
        return videoCode;
      }
      const { data } = await axios.post(
        "https://" +
          process.env.REACT_APP_LMS_URL +
          "/webservice/rest/server.php?wstoken=" +
          process.env.REACT_APP_LMS_TOKEN +
          "&wsfunction=local_course_service_getvideodetails&moodlewsrestformat=json&url=" +
          encodeURIComponent(toVideoUrl(videoCode)),
      );
      let dataArr = [];
      try {
        dataArr = Object.values(JSON.parse(data.data));
      } catch (err) {}
      if (dataArr.length) {
        const urlData = dataArr[0];
        return urlData.video_title;
      } else {
        return videoCode;
      }
    } catch (err) {
      return videoCode;
    }
  }, []);


  // const getVideoName = useCallback(async (videoCode) => {
  //   try {
  //     const { data } = await axios.post(
  //       "https://" +
  //         process.env.REACT_APP_LMS_URL +
  //         "/webservice/rest/server.php?wstoken=" +
  //         process.env.REACT_APP_LMS_TOKEN +
  //         "&wsfunction=local_course_service_getvideodetails&moodlewsrestformat=json&url=" +
  //         toVideoUrl(videoCode),
  //     );
  //     let dataArr = [];
  //     try {
  //       dataArr = Object.values(JSON.parse(data.data));
  //     } catch (err) {}
  //     if (dataArr.length) {
  //       const urlData = dataArr[0];
  //       return urlData.video_title;
  //     } else {
  //       return videoCode;
  //     }
  //   } catch (err) {
  //     return videoCode;
  //   }
  // }, []);

  const [videoData, setVideoData] = useState();
  const [loading, setLoading] = useState(false);
  
  const clearPoll = useCallback(() => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
      pollInterval.current = null;
    }
  }, []);

  const searchVideo = useCallback(async (activityId = null) => {
    const videoCode =
      sourceType === "course-content"
        ? selectedSource.url
        : extractYouTubeVideoIds(urlInputValue)[0];

    if (sourceType === "url" && videoCode) {
      setUrlInputValue(`https://www.youtube.com/embed/${videoCode}`);
    }

    setVideoData();
    setLoading(true);
    try {
      const params = new URLSearchParams({
        video_url: toVideoUrl(videoCode),
      });

      if (activityId) {
        params.append("activity_id", activityId);
      }

      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/video_guide/chapter_generate?${params.toString()}`
      );
      if (data.success) {
        if (data.message.queue_status === "Completed") {
          clearPoll();
        } else if (["Started", "InProgress", "Processing"].includes(data.message.queue_status)) {
          // ✅ resume polling if user comes back and video is still processing
          clearPoll();
          pollInterval.current = setInterval(() => {
            searchVideoRef.current();
          }, 2 * 60 * 1000);
        }
        setVideoData({
          added: data.message.queue_status === "Completed",
          published: +data.message.publish === 1,
          videoCode,
          queueStatus: data.message.queue_status,
          message: data.message.message,
        });
        setCurrentVideoCode(videoCode);
      }

    } catch (err) {
      const queueStatus = err?.response?.data?.message?.queue_status;
      const message = err?.response?.data?.message?.message ?? err?.response?.data?.message;

      if (message && message === "Transcript is available, proceed with chapter generation" && !chapterRegeneratingRef.current) {
        chapterRegenerator(videoCode);
      }

      if (
        err?.response?.data?.success === false &&
        (!["Started", "InProgress", "Processing"].includes(queueStatus) || queueStatus === "Failed")
      ) {
        if (sourceType === "url") {
          if (message === "Video not found in the database") {
            setVideoData({
              added: false,
              published: false,
              videoCode,
              message,
            });
          } else {
            setVideoData({
              added: false,
              published: false,
              videoCode,
              message,
              queueStatus: "Processing",
            });
          }
        } else {
          setVideoData({
            added: false,
            published: false,
            videoCode,
            courseId: selectedSource.course,
            unitId: selectedSource.unit,
            lessonId: lessons.find((l) => l.Id.toString() === selectedSource.lesson)?.cm_id ?? null,
            message,
            ...(message !== "Video not found in the database" && { queueStatus: "Processing" }),
          });
        }
      } else if (["Started", "InProgress", "Processing"].includes(queueStatus)) {
        const { data } = err.response;
        setVideoData({
          added: false,
          published: +data.message.publish === 1,
          videoCode,
          queueStatus: queueStatus,
          message,
        });
      } else {
        setVideoData({
          added: false,
          published: false,
          videoCode,
          message,
        });
      }
    } finally {
      setLoading(false);
      setEnableChapterRegeneration(false);
    }
  }, [sourceType, selectedSource, urlInputValue, clearPoll]);


  // const searchVideo = useCallback(async (activityId = null) => {
  //   const videoCode =
  //     sourceType === "course-content"
  //       ? selectedSource.url
  //       : extractYouTubeVideoIds(urlInputValue)[0];


  //   // ✅ Add this: sync the input back to the clean 11-char video ID
  //   if (sourceType === "url" && videoCode) {
  //     setUrlInputValue(`https://www.youtube.com/embed/${videoCode}`);
  //   }



  //   setVideoData();
  //   setLoading(true);
  //   try {

  //     const params = new URLSearchParams({
  //       video_url: toVideoUrl(videoCode),
  //     });

  //     // ✅ pass activity_id only when available
  //     if (activityId) {
  //       params.append("activity_id", activityId);
  //     }

  //     const { data } = await axios.get(
  //       `${process.env.REACT_APP_API_URL}/video_guide/chapter_generate?${params.toString()}`
  //     );
  //     if (data.success) {
  //       setVideoData({
  //         added: data.message.queue_status === "Completed",
  //         published: +data.message.publish === 1,
  //         videoCode,
  //         queueStatus: data.message.queue_status,
  //         message: data.message.message,
  //       });
  //       setCurrentVideoCode(videoCode);
  //     }
  //   } catch (err) {
  //     const queueStatus = err?.response?.data?.message?.queue_status;
  //     const message = err?.response?.data?.message?.message ?? err?.response?.data?.message;

  //     if (message && message === "Transcript is available, proceed with chapter generation") {
  //       chapterRegenerator(videoCode)
  //     }

  //     if (
  //       err?.response?.data?.success === false &&
  //       (!["Started", "InProgress", "Processing"].includes(queueStatus) || queueStatus === "Failed")
  //     ) {
  //       if (sourceType === "url") {
  //         if (message === "Video not found in the database") {
  //           setVideoData({
  //             added: false,
  //             published: false,
  //             videoCode,
  //             message,
  //           });
  //         } 
  //         // else {
  //         //   const { data } = await axios.post(
  //         //     "https://" +
  //         //       process.env.REACT_APP_LMS_URL +
  //         //       "/webservice/rest/server.php?wstoken=" +
  //         //       process.env.REACT_APP_LMS_TOKEN +
  //         //       "&wsfunction=local_course_service_getvideodetails&moodlewsrestformat=json&url=" +
  //         //       toVideoUrl(videoCode),
  //         //   );
  //         //   let dataArr = [];
  //         //   try {
  //         //     dataArr = Object.values(JSON.parse(data.data));
  //         //   } catch (err) {}
  //         //   if (dataArr.length) {
  //         //     const urlData = dataArr[0];
  //         //     setVideoData({
  //         //       added: false,
  //         //       published: false,
  //         //       videoCode,
  //         //       courseId: urlData.course_id,
  //         //       unitId: urlData.unit_id,
  //         //       lessonId: urlData.lesson_id,
  //         //       message,
  //         //     });
  //         //   } else {
  //         //     toast.error("Video does not exist in the database");
  //         //   }
  //         // }
  //       // }

  //       // if (sourceType === "url") {
  //       //   const { data } = await axios.post(
  //       //     "https://" +
  //       //       process.env.REACT_APP_LMS_URL +
  //       //       "/webservice/rest/server.php?wstoken=" +
  //       //       process.env.REACT_APP_LMS_TOKEN +
  //       //       "&wsfunction=local_course_service_getvideodetails&moodlewsrestformat=json&url=" +
  //       //       toVideoUrl(videoCode),
  //       //   );
  //       //   let dataArr = [];
  //       //   try {
  //       //     dataArr = Object.values(JSON.parse(data.data));
  //       //   } catch (err) {}
  //       //   if (dataArr.length) {
  //       //     const urlData = dataArr[0];
  //       //     setVideoData({
  //       //       added: false,
  //       //       published: false,
  //       //       videoCode,
  //       //       courseId: urlData.course_id,
  //       //       unitId: urlData.unit_id,
  //       //       lessonId: urlData.lesson_id,
  //       //       message,
  //       //     });
  //       //   } else {
  //       //     toast.error("Video does not exist in the database");
  //       //   }
  //       } else {
  //         setVideoData({
  //           added: false,
  //           published: false,
  //           videoCode,
  //           courseId: selectedSource.course,
  //           unitId: selectedSource.unit,
  //           // lessonId: selectedSource.lesson,
  //           lessonId: lessons.find((l) => l.Id.toString() === selectedSource.lesson)?.cm_id ?? null,
  //           message,
  //         });
  //       }
  //     } else if (["Started", "InProgress", "Processing"].includes(queueStatus)) {
  //       const { data } = err.response;
  //       setVideoData({
  //         added: false,
  //         published: +data.message.publish === 1,
  //         videoCode,
  //         queueStatus: queueStatus,
  //         message,
  //       });
  //     } else {
  //       setVideoData({
  //         added: false,
  //         published: false,
  //         videoCode,
  //         message,
  //       });
  //     }
  //     // console.log(err);
  //   } finally {
  //     setLoading(false);
  //     setEnableChapterRegeneration(false);
  //   }
  // }, [sourceType, selectedSource, urlInputValue, clearPoll]);

  const [addingVideo, setAddingVideo] = useState(false);

  const pollInterval = useRef(null);
  const searchVideoRef = useRef(searchVideo);

  useEffect(() => {
    searchVideoRef.current = searchVideo;
  }, [searchVideo]);

const addVideo = useCallback(() => {
  if (!videoData) return;

  setAddingVideo(true);

  const isUrl = sourceType === "url";
  const url = isUrl
    ? `${process.env.REACT_APP_API_URL}/video_guide/direct_chapter_generation`
    : `${process.env.REACT_APP_API_URL}/video_guide/chapter_generate`;

  const payload = isUrl
    ? { video_url: toVideoUrl(videoData.videoCode) }
    : {
        video_url: toVideoUrl(videoData.videoCode),
        course_id: videoData.courseId,
        unit_id: videoData.unitId,
        activity_id: videoData.lessonId,
      };

  // console.log("addVideo payload", payload);
  axios
    .post(url, payload)
    .then(({ data }) => {
      const queueStatus = data.data?.queue_status ?? data.queue_status;
      const message = data.data?.message ?? data.message;

      if (queueStatus === "Completed") {
        searchVideo();
      } else {
        setVideoData((prev) => ({
          ...prev,
          added: false,
          queueStatus: "Started",
          message: message ?? prev.message,
        }));

        clearPoll();
        pollInterval.current = setInterval(() => {
          searchVideoRef.current();
        }, 2 * 60 * 1000);
      }
    })
    .catch((err) => {
      console.log(err);
      const message = err?.response?.data?.message || "Error occurred while adding the video.";
      toast.error(message);
    })
    .finally(() => {
      setAddingVideo(false);
    });
}, [videoData, sourceType, clearPoll, searchVideo]);

  useEffect(() => () => clearPoll(), []);



  // const addVideo = useCallback(() => {
  //   if (!videoData) return;

  //   setAddingVideo(true);
  //   axios
  //     .post(`${process.env.REACT_APP_API_URL}/video_guide/chapter_generate`, {
  //       video_url: toVideoUrl(videoData.videoCode),
  //       course_id: videoData.courseId,
  //       unit_id: videoData.unitId,
  //       activity_id: videoData.lessonId,
  //     })
  //     .then(({ data }) => {
  //       if (data.queue_status === "Completed") {
  //         searchVideo();
  //       } else {
  //         setVideoData((prev) => ({
  //           ...prev,
  //           added: false,
  //           queueStatus: "Started",
  //         }));
  //       }
  //     })
  //     .catch((err) => {
  //       // console.log(err);
  //       toast.error("Error occurred while adding the video.");
  //     })
  //     .finally(() => {
  //       setAddingVideo(false);
  //     });
  // }, [videoData]);

  const [publishingVideo, setPublishingVideo] = useState(false);
  // const publishVideo = useCallback(
  //   (status) => {
  //     if (!videoData) return;

  //     let lessonId = null;
  //     // Only resolve lesson when it exists
  //     if (selectedSource?.lesson) {
  //       const lesson = lessons.find(
  //         (l) => l.Id.toString() === selectedSource.lesson
  //       );
  //       lessonId = lesson?.cm_id; // safe
  //     }

  //     setPublishingVideo(true);
  //     const payload = {
  //       video_url: toVideoUrl(videoData.videoCode),
  //       publish: status,
  //       ...(lessonId && { activity_id: lessonId }),
  //     };

  //     axios
  //       .put(`${process.env.REACT_APP_API_URL}/video_guide/chapter_generate`, payload)
  //       .then(({ data }) => {
  //         setVideoData((prev) => ({
  //           ...prev,
  //           published: status,
  //         }));
  //         searchVideo(lessonId);
  //         toast.success(
  //           status
  //             ? "Successfully added a video guide link on the course unit page."
  //             : "Successfully removed the video guide link on the course unit page.",
  //         );
  //       })
  //       .catch((err) => {
  //         console.log(err);
  //         toast.error(
  //           `Error occurred while ${status ? "publishing" : "unpublishing"} the video guide.`,
  //         );
  //       })
  //       .finally(() => {
  //         setPublishingVideo(false);
  //       });
  //   },
  //   [videoData],
  // );

  const validateUnits = (data) => {
    const temp = data.slice();
    const val = temp.filter((unit) => unit.name.trim() !== "" && unit.name !== ".");
    return val;
  };

  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showPromptsModal, setPromptsModal] = useState(false);

  const isTeacher = useMemo(() => isTeacherRole(), []);

  const getCategory = async () => {
    const res = await axios.post(
      "https://" +
        process.env.REACT_APP_LMS_URL +
        "/webservice/rest/server.php?wstoken=" +
        process.env.REACT_APP_LMS_TOKEN +
        "&wsfunction=local_course_service_get_category_list&moodlewsrestformat=json"
    );

    return res.data.data;
  };

  const { data : categories=[], isPending: isCategoryPending, isError: isCategoryError, isSuccess: isCategorySuccess } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategory,
  });

  const filteredCategory = categories?.filter(
    (item) => item.Category_ID === 28 || item.Category_ID === 38
  );


    // useEffect(() => {
    //   axios
    //     .post(
    //       "https://" +
    //         process.env.REACT_APP_LMS_URL +
    //         "/webservice/rest/server.php?wstoken=" +
    //         process.env.REACT_APP_LMS_TOKEN +
    //         "&wsfunction=local_course_service_get_category_list&moodlewsrestformat=json",
    //     )
    //     .then((response) => {
    //       if (response.data) {
    //         const { data } = response.data;
    //         const filteredCategory = data.filter(item => 
    //           item.Category_ID === 28 || item.Category_ID === 38
    //         );
    //         setCategoryArray(filteredCategory);
    //       }
    //     })
    //     .catch((err) => {
    //       console.log(err);
    //     });
    //   // }
    // }, []);


  const getCourses = async () => {
    let url =
      ("https://" +
        process.env.REACT_APP_LMS_URL +
        "/webservice/rest/server.php?wstoken=" +
        process.env.REACT_APP_LMS_TOKEN +
        "&wsfunction=local_course_service_course_data&moodlewsrestformat=json&user_id=0&category_id=" +
        selectedSource.category
      );
    if (isTeacher) {
      const userId = localStorage.getItem("user_id");
      url += "&user_id=" + userId;
    }

    if (!selectedSource.category) return;
    const response = await axios.post(url)

    return response.data.data
  }

 const {data: courses = [], isFetching: isCourseFetching, isError: isCourseError, isSuccess: isCourseSuccess } =  useQuery ({
    queryKey : ['courses', selectedSource.category],
    queryFn : getCourses,
    enabled: !!selectedSource.category && selectedSource.category !== "0"
  })

  // useEffect(() => {
  //   let url =
  //     ("https://" +
  //     process.env.REACT_APP_LMS_URL +
  //     "/webservice/rest/server.php?wstoken=" +
  //     process.env.REACT_APP_LMS_TOKEN +
  //     "&wsfunction=local_course_service_course_data&moodlewsrestformat=json&user_id=0&category_id=" +
  //     selectedSource.category
  //     );
  //   if (isTeacher) {
  //     const userId = localStorage.getItem("user_id");
  //     url += "&user_id=" + userId;
  //   }

  //   if (!selectedSource.category) return;

  //   axios
  //     .post(url)
  //     .then((response) => {
  //       setCourseArray(response.data.data);
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //     });
  // }, [selectedSource.category]);


  const getUnits = async () => {
    const resp = await axios.post(
        "https://" +
          process.env.REACT_APP_LMS_URL +
          "/webservice/rest/server.php?wstoken=" +
          process.env.REACT_APP_LMS_TOKEN +
          "&wsfunction=local_course_service_course_unit_data&moodlewsrestformat=json&course_id=" +
          selectedSource.course,
      )
        // 🔥 Transform data here
      const decodedData = JSON.parse(resp.data.data);
      const units = decodedData?.[0]?.units || [];

      return validateUnits(units);

  }

  const {data: units = [], isFetching: isUnitFetching, isError: isUnitError, isSuccess: isUnitSuccess } =  useQuery ({
    queryKey : ['units', selectedSource.course],
    queryFn: getUnits,
    enabled: !!selectedSource.course && selectedSource.course !== "0"
  })

  // useEffect(() => {
  //   if (!selectedSource.course) return;
  //   axios
  //     .post(
  //       "https://" +
  //         process.env.REACT_APP_LMS_URL +
  //         "/webservice/rest/server.php?wstoken=" +
  //         process.env.REACT_APP_LMS_TOKEN +
  //         "&wsfunction=local_course_service_course_unit_data&moodlewsrestformat=json&course_id=" +
  //         selectedSource.course,
  //     )
  //     .then((response) => {
  //       if (response.data) {
  //         const decodedData = JSON.parse(response.data.data);
  //         let units = decodedData[0].units;
  //         setUnitArray(validateUnits(units));
  //       }
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //     });
  // }, [selectedSource.course]);

  const getLessons = async () => {
  const resp = await axios.post(
        "https://" +
          process.env.REACT_APP_LMS_URL +
          "/webservice/rest/server.php?wstoken=" +
          process.env.REACT_APP_LMS_TOKEN +
          "&wsfunction=local_course_service_unit_lessions_data&moodlewsrestformat=json&unit_id=" +
          selectedSource.unit,
      )
      return resp.data.data;
  }

  const {data: lessons = [], isFetching: isLessonFetching, isError: isLessonError, isSuccess: isLessonSuccess } =  useQuery ({
    queryKey: ['lessons', selectedSource.unit],
    queryFn: getLessons,
    enabled: !!selectedSource.unit && selectedSource.unit !== "0"
  })

    // Only resolve lesson when it exists
  if (selectedSource?.lesson) {
    const lesson = lessons.find(
      (l) => l.Id.toString() === selectedSource.lesson
    );
    lessonId = lesson?.cm_id; // ✅
  }


  const publishVideo = async ({ videoCode, lessonId, status }) => {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/video_guide/chapter_generate`, {
      video_url: `https://www.youtube.com/embed/${videoCode}`,
      publish: status,
      ...(lessonId && { activity_id: lessonId }),
    }
    );
    return response.data;
  }

  const { mutate: publishVideoMutation, isPending: isPublishingVideo } = useMutation({
    mutationKey: ['publishVideo', lessonId],
    mutationFn: publishVideo,
    onSuccess: (_, variables) => {
      const { status, lessonId } = variables;

      setVideoData((prev) => ({
        ...prev,
        published: status,
      }));
      searchVideo(lessonId);

      toast.success(
        status
          ? "Successfully added a video guide link on the course unit page."
          : "Successfully removed the video guide link on the course unit page.",
      );
    },
    onError: (_, variables) => {
      const { status } = variables;
      toast.error(
        `Error occurred while ${status ? "publishing" : "unpublishing"} the video guide.`,
      );
    },
  })



  const handlePublish = (status) => {
    if (!videoData) return;

    let lessonId = null;
    // Only resolve lesson when it exists
    if (selectedSource?.lesson) {
      const lesson = lessons.find(
        (l) => l.Id.toString() === selectedSource.lesson
      );
      lessonId = lesson?.cm_id; // safe
    }

    publishVideoMutation({ lessonId, status, videoCode: videoData.videoCode });
  }

  // const publishVideo1 = useCallback(
  //   (status) => {
  //     if (!videoData) return;

  //     let lessonId = null;
  //     // Only resolve lesson when it exists
  //     if (selectedSource?.lesson) {
  //       const lesson = lessons.find(
  //         (l) => l.Id.toString() === selectedSource.lesson
  //       );
  //       lessonId = lesson?.cm_id; // safe
  //     }

  //     setPublishingVideo(true);
  //     const payload = {
  //       video_url: toVideoUrl(videoData.videoCode),
  //       publish: status,
  //       ...(lessonId && { activity_id: lessonId }),
  //     };

  //     axios
  //       .put(`${process.env.REACT_APP_API_URL}/video_guide/chapter_generate`, payload)
  //       .then(({ data }) => {
  //         setVideoData((prev) => ({
  //           ...prev,
  //           published: status,
  //         }));
  //         searchVideo(lessonId);
  //         toast.success(
  //           status
  //             ? "Successfully added a video guide link on the course unit page."
  //             : "Successfully removed the video guide link on the course unit page.",
  //         );
  //       })
  //       .catch((err) => {
  //         console.log(err);
  //         toast.error(
  //           `Error occurred while ${status ? "publishing" : "unpublishing"} the video guide.`,
  //         );
  //       })
  //       .finally(() => {
  //         setPublishingVideo(false);
  //       });
  //   },
  //   [videoData],
  // );

  // useEffect(() => {
  //   if (!selectedSource.unit) return;
  //   axios
  //     .post(
  //       "https://" +
  //         process.env.REACT_APP_LMS_URL +
  //         "/webservice/rest/server.php?wstoken=" +
  //         process.env.REACT_APP_LMS_TOKEN +
  //         "&wsfunction=local_course_service_unit_lessions_data&moodlewsrestformat=json&unit_id=" +
  //         selectedSource.unit,
  //     )
  //     .then((response) => {
  //       if (response.data) {
  //         setLessonArray(response.data.data);
  //       }
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //     });
  // }, [selectedSource.unit]);
  

const getVideos = async ({ queryKey }) => {
  const [, lessonId] = queryKey; // ✅ extract lessonId
  // find lesson
  const lesson = lessons.find(
    (l) => l.Id.toString() === lessonId
  );

  if (!lesson) return [];

  const urls = extractYouTubeVideoIds(lesson.Lession_Content);

  const names = await Promise.all(
    urls.map((url) => getVideoName(url))
  );

  return urls.map((url, i) => ({
    url,
    name: names[i],
  }));
  }

const {
  data: videos = [],
  isFetching: isVideoFetching,
  isError: isVideoError,
  isSuccess: isVideoSuccess,
} = useQuery({
  queryKey: ['videos', selectedSource.lesson],
  queryFn: getVideos,
  enabled: !!selectedSource.lesson && selectedSource.lesson !== "0",
});

// console.log('videos', videos)

  // useEffect(() => {
  //   if (!selectedSource.lesson) return;
  //   const lesson = lessons.find((l) => l.Id.toString() === selectedSource.lesson);
  //   if (!lesson) return;
  //   const urls = extractYouTubeVideoIds(lesson.Lession_Content);
  //   setUrlArray([]);
  //   Promise.all(urls.map((url) => getVideoName(url))).then((names) => {
  //     setUrlArray(
  //       urls.map((url, i) => ({
  //         url,
  //         name: names[i],
  //       })),
  //     );
  //   });
  // }, [selectedSource.lesson]);

  const [regeneratingChapter, setRegeneratingChapter] = useState(false);

  const regenerateQuestions = useCallback(
    (videoCode) => {
      setDeleting(true);
      let url = `${process.env.REACT_APP_API_URL}/video_guide`;
      // if (tableView === "transcript") {
      //   url = `${process.env.REACT_APP_API_URL}/video_guide_re_chapter`;
      // } else if (tableView === "content") {
      //   url = `${process.env.REACT_APP_API_URL}/video_guide`;
      // }
      if (!url) return;
      setRegeneratingChapter(true);
      axios
        .put(url, { video_url: `https://www.youtube.com/embed/${videoCode}` })
        .then((response) => {
          toast.success("Regeneration successful. Check new questions.");
          searchVideo();
          setCurrentVideoCode("");
          setTimeout(() => setCurrentVideoCode(videoCode), 1000);
        })
        .catch((err) => {
          toast.error("Error occured while regenerating chapter.");
          // console.log(err);
        })
        .finally(() => {
          setDeleting(false);
          setQuestionConfirmation(false);
          setRegeneratingChapter(false);
        });
    },
    [tableView, searchVideo],
  );

  const ref = useRef();
  const scrollRef = useRef();

  const [enableChapterRegeneration, setEnableChapterRegeneration] = useState(false);

  const [playing, setPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const timeout = useRef();

  const chapterRegenerator = useCallback(
    (videoCode) => {
      setDeleting(true);
      chapterRegeneratingRef.current = true;
      const videoUrl = `https://www.youtube.com/embed/${videoCode}`;
      const putUrl = `${process.env.REACT_APP_API_URL}/video_guide_re_chapter`;

      // Fire the PUT request to kick off the background job
      axios
        .put(putUrl, { video_url: videoUrl })
        .catch(() => {
          // PUT may 504 since backend moved to queue — that's expected, ignore it
        });

      // Start polling GET every 1 minute for the result
      toast.info("Chapter regeneration started. Polling for result...");

      // Clear any existing poll
      if (chapterPollIntervalRef.current) {
        clearInterval(chapterPollIntervalRef.current);
      }

      const pollUrl = `${process.env.REACT_APP_API_URL}/video_guide_re_chapter`;

      chapterPollIntervalRef.current = setInterval(() => {
        axios
          .get(pollUrl, { params: { video_url: videoUrl } })
          .then((response) => {
            if (response.data?.success === true) {
              // Stop polling
              clearInterval(chapterPollIntervalRef.current);
              chapterPollIntervalRef.current = null;

              toast.success("Regeneration successful. Check new questions.");
              searchVideo();
              setCurrentVideoCode("");
              setTimeout(() => setCurrentVideoCode(videoCode), 1000);

              setDeleting(false);
              chapterRegeneratingRef.current = false;
              setChapterConfirmation(false);
            }
          })
          .catch(() => {
            // Polling attempt failed — keep trying on next interval
          });
      }, 60000); // Poll every 60 seconds
    },
    [searchVideo],
  );


  const handleEnableChapterRegeneration = useCallback(() => {
    setEnableChapterRegeneration(true);
  }, []);

  return (
    <>
      <Container className="my-3">
        <div className="d-flex gap-4 mb-2 align-items-center">
          <Form.Check
            type="radio"
            name="sourceType"
            checked={sourceType === "url"}
            onChange={(e) => setSourceType("url")}
            label="Direct URL"
          />
          <Form.Check
            type="radio"
            name="sourceType"
            checked={sourceType === "course-content"}
            onChange={(e) => setSourceType("course-content")}
            label="Lesson"
          />
          <div
            style={{
              textAlign: "left",
              alignItems: "center",
              marginLeft: "auto",
            }}
          >
            {videoData?.message ? (
              <>
                <span style={{ fontWeight: "bold" }}>Status: </span>
                {videoData?.message}
              </>
            ) : (
              ""
            )}
          </div>
        </div>
        <Row ref={scrollRef} className="align-items-center">
          {sourceType === "url" && (
            <Col md="6" lg="8s">
              <Form.Group controlId="url-textbox" className="d-flex align-items-center mb-2">
                <Form.Control
                  type="text"
                  placeholder="Enter URL"
                  style={{ maxWidth: "100%" }}
                  value={urlInputValue}
                  onChange={(e) => setUrlInputValue(e.target.value)}
                />
              </Form.Group>
            </Col>
          )}
          {sourceType === "course-content" && (
            <>
             <Col md="6" lg="4">  
             <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    aria-label="Default select example"
                    value={selectedSource.category}
                    onChange={(e) =>
                      setSelectedSource((s) => ({
                        ...s,
                        category: e.target.value,
                        course: "",
                        unit: "",
                        lesson: "",
                        url: "",
                      }))
                    }
                  >
                    <option value="0">{isCategoryPending ? "Loading Categories..." : isCategoryError ? "Failed to load Categories" : "Select Category"}</option>
                    {/* <option value="0">Select Category</option> */}
                    {isCategorySuccess && 
                      filteredCategory.map((category) => (
                        <option key={category.Category_ID} value={category.Category_ID}>
                          {category.Category_Name}
                        </option>
                    ))}
                  </Form.Select>
              </Form.Group>
             </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.course}
                    onChange={(e) =>
                      setSelectedSource((s) => ({
                        ...s,
                        course: e.target.value,
                        unit: "",
                        lesson: "",
                        url: "",
                      }))
                    }
                  >
                    <option>
                      {isCourseFetching
                        ? "Loading Courses..."
                        : isCourseError
                          ? "Failed to load Courses"
                          : "Select Course"}
                    </option>
                    {isCourseSuccess &&
                      courses.map((course) => (
                        <option key={course.Course_ID} value={course.Course_ID}>
                          {course.Course_Name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.unit}
                    onChange={(e) => setSelectedSource((s) => ({ ...s, unit: e.target.value, lesson: "", url: "" }))}
                  >
                    <option>{
                      isUnitFetching
                        ? "Loading Units..."
                        : isUnitError
                          ? "Failed to load Units"
                          : "Select Unit"}</option>
                    { isUnitSuccess &&
                      units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.lesson}
                    onChange={(e) =>
                      setSelectedSource((s) => ({
                        ...s,
                        lesson: e.target.value,
                        url: "",
                      }))
                    }
                  >
                    <option>{
                      isLessonFetching
                        ? "Loading Lessons..."
                        : isLessonError
                          ? "Failed to load Lessons"
                          : "Select Lesson"}</option>

                    { isLessonSuccess &&
                      lessons.map((lesson) => (
                        <option key={lesson.Id} value={lesson.Id}>
                          {lesson.Lession_Name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.url}
                    onChange={(e) => setSelectedSource((s) => ({ ...s, url: e.target.value }))}
                  >
                    <option>{
                      isVideoFetching
                        ? "Loading URLs..."
                        : isVideoError
                          ? "Failed to load URLs"
                          : "Select URL"}</option>
                    {isVideoSuccess &&
                      videos.map((url) => (
                        <option key={url.url} value={url.url}>
                          {url.name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </>
          )}
          <Col md="6" lg="4" style={{ display: "flex", gap: "8px" }}>
            <Button
              variant="outline-dark"
              disabled={sourceType === "course-content" ? !selectedSource.url : !urlInputValue}
              onClick={() => searchVideo(lessonId)}
              style={{ height: "48px" }}
            >
              Search
            </Button>
            <Button
              variant="outline-dark"
              onClick={() => {
                setSelectedSource({
                  category: "",
                  lesson: "",
                  course: "",
                  unit: "",
                  url: "",
                });
                setUrlInputValue("");
                setVideoData();
              }}
              style={{ height: "48px" }}
            >
              Clear
            </Button>
          </Col>
        </Row>
        {loading && <div style={{ margin: "40px 0" }}> 
                          <Button
                            variant="link"
                            className="mt-2 mb-5 btn-spinner ms-0 text-decoration-none"
                            disabled
                          >
                            <Spinner
                              as="span"
                              animation="grow"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-1"
                            />
                          </Button></div>}
        {!!videoData && !videoData.added && (
          <div className="d-flex gap-2 mb-2" style={{ textAlign: "left" }}>
            {["Started", "InProgress", "Processing"].includes(videoData.queueStatus) ? (
              "The video is currently being processed for the Video Guide. You will be able to review the content and questions once it is ready."
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  addVideo();
                }}
                style={{ height: "48px" }}
                disabled={addingVideo}
              >
                {addingVideo ? (
                  <div className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    Adding Video
                  </div>
                ) : (
                  "Add Video"
                )}
              </Button>
            )}
          </div>
        )}
        {!!videoData && videoData.added && (
          <>
            <div className="d-flex gap-2 mt-1 mb-2 align-items-center">
              <Button
                variant="primary"
                onClick={() => {
                  if (sourceType === "course-content") {
                    setCurrentVideoCode(selectedSource.url);
                  } else {
                    const videoCodes = extractYouTubeVideoIds(urlInputValue);
                    if (videoCodes.length) setCurrentVideoCode(videoCodes[0]);
                  }
                  setTableView("transcript");
                }}
                disabled={sourceType === "course-content" ? !selectedSource.url : !urlInputValue}
                style={{ height: "48px" }}
                className={tableView === "transcript" ? "btn-active" : ""}
              >
                Video Transcript
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (sourceType === "course-content") {
                    setCurrentVideoCode(selectedSource.url);
                  } else {
                    const videoCodes = extractYouTubeVideoIds(urlInputValue);
                    if (videoCodes.length) setCurrentVideoCode(videoCodes[0]);
                  }
                  setTableView("content");
                }}
                disabled={sourceType === "course-content" ? !selectedSource.url : !urlInputValue}
                style={{ height: "48px" }}
                className={tableView === "content" ? "btn-active" : ""}
              >
                Chapter Content
              </Button>
              {!isTeacher && (
                <Button
                  variant="primary"
                  onClick={() => setPromptsModal(true)}
                  style={{ height: "48px" }}
                  disabled={sourceType === "course-content" ? !selectedSource.url : !urlInputValue}
                >
                  Edit Prompt
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => {
                  setShowQuestionsModal(true);
                }}
                style={{ height: "48px" }}
                disabled={sourceType === "course-content" ? !selectedSource.url : !urlInputValue}
              >
                View Questions
              </Button>
                {(isAdmin || hasEdit(2)) &&
              <Button
                variant="primary"
                onClick={() => setQuestionConfirmation(true)}
                style={{ height: "48px" }}
                // disabled={
                //   !enableChapterRegeneration ||
                //   regeneratingChapter ||
                //   !tableView ||
                //   (sourceType === "course-content"
                //     ? !selectedSource.url
                //     : !urlInputValue)
                // }
              >
                {regeneratingChapter ? (
                  <div className="d-flex align-items-center gap-2 ms-2">
                    <Spinner animation="border" size="sm" />
                    Loading
                  </div>
                ) : (
                  "Questions Regenerator"
                )}
              </Button>
                }
                {(isAdmin || hasEdit(2)) &&
              <Button variant="primary" onClick={() => setChapterConfirmation(true)}>
                Chapter Regenerator
              </Button>
                }               
                {(isAdmin || hasEdit(2)) &&
              <Button
                variant="primary"
                onClick={() => {
                  handlePublish(!videoData.published);
                }}
                style={{ height: "48px" }}
                disabled={
                  (!videoData.published && !videoData.message.includes("ready for publish")) ||
                  isPublishingVideo
                }
              >
                {!videoData.published ? "Publish" : "Unpublish"}
              </Button>
                }   
            </div>
            <Row>
              <Col xs="12">
                {!!currentVideoCode && (
                  // <ReactPlayer
                  //   ref={ref}
                  //   controls
                  //   url={`https://www.youtube.com/watch?v=${currentVideoCode}`}
                  //   width={"100%"}
                  //   playing={playing}
                  //   onPlay={() => {
                  //     if (!playing) {
                  //       clearTimeout(timeout.current);
                  //       setPlaying(true);
                  //     }
                  //   }}
                  //   onPause={() => {
                  //     if (playing) {
                  //       clearTimeout(timeout.current);
                  //       setPlaying(false);
                  //     }
                  //   }}
                  //   onProgress={({ playedSeconds }) => {
                  //     setVideoProgress(playedSeconds);
                  //   }}
                  // />
                  <div
                    style={{
                      maxWidth: "1200px",
                      margin: "auto",
                      padding: "20px",
                    }}
                  >
                    <ReactPlayer
                      ref={ref}
                      url={`https://www.youtube.com/watch?v=${currentVideoCode}`}
                      playing={playing}
                      controls={false}
                      onProgress={handleProgress}
                      onDuration={handleDuration}
                      width="100%"
                      height="400px"
                    />

                    {/* Custom Controls */}
                    <div
                      style={{
                        background: "#111",
                        color: "#fff",
                        padding: "10px",
                        marginTop: "10px",
                        borderRadius: "6px",
                      }}
                    >
                      <button onClick={togglePlayPause} style={{ marginRight: "10px" }}>
                        {playing ? "Pause" : "Play"}
                      </button>

                      <input
                        type="range"
                        min={0}
                        max={1}
                        step="0.01"
                        value={played}
                        onChange={handleSeekChange}
                        style={{ width: "60%", verticalAlign: "middle" }}
                      />

                      <span style={{ marginLeft: "10px" }}>
                        {/* {Math.floor(playedSeconds * 1000)}ms / {Math.floor(duration * 1000)}ms */}
                        {playedSeconds.toFixed(3)}s / {duration.toFixed(3)}s
                      </span>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
            {tableView === "transcript" && (
              <TranscriptTable
                videoCode={currentVideoCode}
                scrollRef={scrollRef}
                onPlayClick={(startSeconds, endSeconds) => {
                  ref.current.seekTo(startSeconds, "seconds");
                  setPlaying(true);
                  clearTimeout(timeout.current);
                  timeout.current = setTimeout(() => {
                    setPlaying(false);
                  }, (endSeconds - startSeconds) * 1000);
                }}
                videoProgress={videoProgress}
                enableChapterRegeneration={handleEnableChapterRegeneration}
                isAdmin={isAdmin}
                hasEdit={hasEdit(2)}
              />
            )}
            {tableView === "content" && (
              <ContentTable
                videoCode={currentVideoCode}
                scrollRef={scrollRef}
                onPlayClick={(startSeconds, endSeconds) => {
                  ref.current.seekTo(startSeconds, "seconds");
                  setPlaying(true);
                  clearTimeout(timeout.current);
                  timeout.current = setTimeout(() => {
                    setPlaying(false);
                  }, (endSeconds - startSeconds) * 1000);
                }}
                videoProgress={videoProgress}
                enableChapterRegeneration={handleEnableChapterRegeneration}
                isAdmin={isAdmin}
                hasEdit={hasEdit(2)}
              />
            )}
            {showQuestionsModal && (
              <ViewQuestionsModal
                onClose={() => setShowQuestionsModal(false)}
                videoCode={
                  sourceType === "course-content"
                    ? selectedSource.url
                    : extractYouTubeVideoIds(urlInputValue)[0]
                }
                isAdmin={isAdmin}
                hasEdit={hasEdit(2)}
              />
            )}
            {showPromptsModal && (
              <EditPromptModal
                onClose={() => setPromptsModal(false)}
                videoCode={
                  sourceType === "course-content"
                    ? selectedSource.url
                    : extractYouTubeVideoIds(urlInputValue)[0]
                }
                enableChapterRegeneration={() => setEnableChapterRegeneration(true)}
                isAdmin={isAdmin}
                hasEdit={hasEdit(2)}
              />
            )}
            {(questionConfirmation || chapterConfirmation) && (
              <DeleteConfirmationModal
                loading={deleting}
                onClose={() =>
                  questionConfirmation
                    ? setQuestionConfirmation(false)
                    : setChapterConfirmation(false)
                }
                onConfirm={() =>
                  questionConfirmation
                    ? regenerateQuestions(
                        sourceType === "course-content"
                          ? selectedSource.url
                          : extractYouTubeVideoIds(urlInputValue)[0],
                      )
                    : chapterRegenerator(
                        sourceType === "course-content"
                          ? selectedSource.url
                          : extractYouTubeVideoIds(urlInputValue)[0],
                      )
                }
                description={
                  questionConfirmation
                    ? "The existing question might change when regenerating questions. Are you sure you want to proceed?"
                    : "The existing question might change when regenerating the chapter. Are you sure you want to proceed?"
                }
              />
            )}
          </>
        )}
      </Container>
    </>
  );
};

export function extractYouTubeVideoIds(text) {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/g;
  const videoIds = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    videoIds.push(match[1]);
  }

  return videoIds;
}

export function toVideoUrl(videoCode) {
  return `https://www.youtube.com/embed/${videoCode}`;
}

export default VideoGuideSettings;
